/**
 * formatters.js - Shared formatting utilities
 * Extracted from Analysis.jsx for reuse across components
 */

/**
 * Converts snake_case or camelCase column names to human-readable "Title Case"
 * @param {string} columnName - The column name to humanize
 * @returns {string} Human-readable version
 */
export const humanizeColumnName = (columnName) => {
  if (!columnName) return '';
  
  // Handle snake_case
  let result = columnName.replace(/_/g, ' ');
  
  // Handle camelCase by inserting space before uppercase letters
  result = result.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Capitalize first letter of each word
  result = result.replace(/\b\w/g, (char) => char.toUpperCase());
  
  return result.trim();
};

/**
 * Format a number as percentage with specified decimal places
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format a number with thousands separators
 * @param {number} value - The value to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Number(value).toLocaleString();
};

/**
 * Compact number formatting: 1,500 → 1.5k, 12,000 → 12k, 1,200,000 → 1.2M.
 * Falls back to thousands-separated for values < 1,000.
 *
 * @param {number} value
 * @param {number} threshold - minimum value to apply compact form (default: 1000)
 */
export const formatCompact = (value, threshold = 1000) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  const n = Number(value);
  if (Math.abs(n) < threshold) return n.toLocaleString();
  if (Math.abs(n) < 1_000_000) {
    const k = n / 1_000;
    return `${k.toFixed(k >= 10 ? 0 : 1)}k`;
  }
  const m = n / 1_000_000;
  return `${m.toFixed(m >= 10 ? 0 : 1)}M`;
};

/**
 * Score / mean formatter: trim trailing zeros for a clean enterprise look.
 * 2.8164 → "2.8", 2.0 → "2.0", 0.16 → "0.2"
 */
export const formatScore = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return Number(value).toFixed(decimals);
};

/**
 * Signed delta: +0.16, -0.04, ±0.00
 */
export const formatDelta = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const n = Number(value);
  const s = Math.abs(n).toFixed(decimals);
  if (n > 0) return `+${s}`;
  if (n < 0) return `−${s}`;
  return `±${s}`;
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength) + '...';
};

/**
 * Format a date value (ISO string, Date, or timestamp) as a localized date string.
 * Returns null when input is falsy or invalid.
 *
 * @param {string|Date|number} value - The date to format
 * @param {'short'|'long'} monthStyle - 'short' for Jan, 'long' for January (default 'short')
 * @returns {string|null}
 */
export const formatDate = (value, monthStyle = 'short') => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: monthStyle,
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};
